import { Page, View } from "@react-pdf/renderer";
import { HtmlToPdf } from "@/components/PdfDocument/HtmlToPdf";
import { getStaticHeaderSectionPaddingYStyle } from "@/components/PdfDocument/utils/getStyles";
import { resumeThemes } from "@/constants/resume";
import {
    WithDatesSectionItem,
    FullWidthSectionItem,
    SectionTitle,
} from "@/pages/LivePreview/components/LiveSections";
import {
    liveProfileStyles,
    liveResumeStyles,
    baseLiveResumeStyles as base,
    liveSectionStyles,
} from "@/pages/LivePreview/liveResumeStyles";
import { IResumePayload } from "@/pages/LivePreview/types";
import { ResumeStyles } from "@/types/resume";

interface StudentTemplateProps {
    resumeData: IResumePayload;
    styles: ResumeStyles;
}

export const StudentTemplate = ({
    resumeData,
    styles,
}: StudentTemplateProps) => {
    const { profile, summary, experience, education, skills } = resumeData;
    const documentWrapperStyles = {
        fontFamily: styles?.GLOBAL?.fontFamily,
    };

    const pageStyles = {
        ...base.page,
        ...documentWrapperStyles,
    };

    // Purposefully omitting the marginTop as I do not know which is page 1 or 2 this way, so I am letting the PDF library handle it.
    const profilePaddingYStyle = getStaticHeaderSectionPaddingYStyle(
        styles.GLOBAL.sectionSpacingSize,
    );

    const theme = resumeThemes.find(theme => {
        return theme.profileBackgroundColor === styles.GLOBAL.themeColor;
    });
    const profileBackgroundColour =
        theme?.profileBackgroundColor ?? styles.GLOBAL.backgroundColor;

    // Support full width background colour for the profile section.
    const profileContainerStyles = {
        backgroundColor: profileBackgroundColour,
        marginLeft: -base.page.padding,
        marginTop: -base.page.padding,
        marginRight: -base.page.padding,
        paddingLeft: base.page.padding,
        paddingRight: base.page.padding,
        paddingTop: base.page.padding,
        paddingBottom: profilePaddingYStyle.paddingBottom,
        marginBottom: profilePaddingYStyle.marginBottom,
    };

    return (
        <Page
            style={pageStyles}
            size="A4"
        >
            {/* Full width profile container */}
            <View style={{ ...base.verticalFlex, ...profileContainerStyles }}>
                <HtmlToPdf
                    html={`<h1 style="text-align: center;">${profile.name}</h1><h3 style="text-align: center;">${profile.jobTitle}</h3>`}
                    documentStyles={styles}
                />
                <View
                    style={{
                        ...base.horizontalFlex,
                        ...base.centredFlex,
                    }}
                >
                    <HtmlToPdf
                        html={`<p>${profile.phone}</p>`}
                        documentStyles={styles}
                    />
                    <HtmlToPdf
                        html={`<p>${profile.email}</p>`}
                        documentStyles={styles}
                    />

                    <HtmlToPdf
                        html={`<p>${profile.location}</p>`}
                        documentStyles={styles}
                    />
                    <HtmlToPdf
                        html={`<a href="${profile.linkUrl}"><p>${profile.linkText}</p></a>`}
                        documentStyles={styles}
                    />
                </View>
            </View>

            {/* Sections container */}
            <View
                style={{
                    ...liveResumeStyles.columnContainer,
                    ...liveSectionStyles.sectionContainer,
                }}
            >
                {/* Summary */}
                <SectionTitle
                    title={summary?.title || ""}
                    templateStyle={styles}
                />
                <FullWidthSectionItem
                    description={summary?.description}
                    templateStyle={styles}
                    isLastItem={true}
                />
            </View>

            {/* Experience */}
            <View
                style={{
                    ...liveResumeStyles.columnContainer,
                    ...liveSectionStyles.sectionContainer,
                }}
            >
                <SectionTitle
                    title={experience?.title}
                    templateStyle={styles}
                />
                {experience?.items.map((item, index) => (
                    <WithDatesSectionItem
                        key={item.company}
                        title={item.company}
                        subtitle={item.position}
                        additionalTitle={item.companyDescription}
                        datePeriod={item.datePeriod}
                        location={item.location}
                        description={item.description}
                        templateStyle={styles}
                        isLastItem={index === experience?.items.length - 1}
                    />
                ))}
            </View>

            {/* Education */}
            <View
                style={{
                    ...liveResumeStyles.columnContainer,
                    ...liveSectionStyles.sectionContainer,
                }}
            >
                <SectionTitle
                    title={education?.title}
                    templateStyle={styles}
                />
                {education?.items.map((item, index) => (
                    <WithDatesSectionItem
                        key={item.institution}
                        title={item.institution}
                        subtitle={item.fieldOfStudy}
                        additionalTitle={item.grade}
                        datePeriod={item.datePeriod}
                        location={item.location}
                        description={item.description}
                        templateStyle={styles}
                        isLastItem={index === education?.items.length - 1}
                    />
                ))}
            </View>

            {/* Skills */}
            <View
                style={{
                    ...liveResumeStyles.columnContainer,
                    ...liveSectionStyles.sectionContainer,
                }}
            >
                <SectionTitle
                    title={skills?.title}
                    templateStyle={styles}
                />
                {skills?.items.map((item, index) => (
                    <FullWidthSectionItem
                        key={item.category}
                        title={item.category}
                        description={item.description}
                        templateStyle={styles}
                        isLastItem={index === skills?.items.length - 1}
                    />
                ))}
            </View>
        </Page>
    );
};
